import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Trash2, X } from "lucide-react";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import { FormDropdown } from "../FormDropdown/FormDropdown";
import { Category, fetchCategoryList } from "../../api/category";
import {
  CHECK_TYPE_KEYS,
  CHECK_TYPE_LABELS,
  createPricelist,
  fetchPricelistById,
  type CheckTypeKey,
  updatePricelist,
} from "../../api/pricelist";
import { useNotification } from "../Notification/NotificationProvider";
import { ModalPortal } from "../ModalPortal/ModalPortal";
import {
  PAYMENT_PERIOD_OPTIONS,
  PRICELIST_STATUS_OPTIONS,
} from "../../utils/pricelistFormat";
import {
  type CheckTypeFormValues,
  type LimitFormRow,
  type PricelistFormValues,
  emptyLimitRow,
  emptyPricelistForm,
  formToPricelist,
  pricelistToForm,
  validatePricelistForm,
} from "./pricelistForm";
import "../CategoryModal/CategoryModal.scss";
import "./PricelistModal.scss";

interface PricelistModalProps {
  pricelistId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

export const PricelistModal = ({ pricelistId, onClose, onSaved }: PricelistModalProps) => {
  const { showNotification } = useNotification();
  const isCreate = !pricelistId;
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(isCreate);
  const [form, setForm] = useState<PricelistFormValues>(emptyPricelistForm);
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    let cancelled = false;

    fetchCategoryList()
      .then((items) => {
        if (!cancelled) setCategories(items);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!pricelistId) {
      setForm(emptyPricelistForm());
      setIsEditing(true);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setIsEditing(false);

    fetchPricelistById(pricelistId)
      .then((item) => {
        if (!cancelled) setForm(pricelistToForm(item));
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pricelistId, showNotification]);

  const readOnly = !isEditing;

  const categoryOptions = useMemo(
    () =>
      categories
        .filter((category): category is Category & { id: string } => Boolean(category.id))
        .map((category) => ({
          id: category.id,
          label: category.name?.trim() || category.id,
        })),
    [categories]
  );

  const updatePriceRow = useCallback(
    (index: number, patch: Partial<PricelistFormValues["priceItemList"][0]>) => {
      setForm((current) => ({
        ...current,
        priceItemList: current.priceItemList.map((row, rowIndex) =>
          rowIndex === index ? { ...row, ...patch } : row
        ),
      }));
    },
    []
  );

  const updateCheckType = useCallback(
    (key: CheckTypeKey, patch: Partial<CheckTypeFormValues>) => {
      setForm((current) => ({
        ...current,
        checkTypes: {
          ...current.checkTypes,
          [key]: { ...current.checkTypes[key], ...patch },
        },
      }));
    },
    []
  );

  const updateCheckTypeLimit = useCallback(
    (key: CheckTypeKey, index: number, patch: Partial<LimitFormRow>) => {
      setForm((current) => {
        const typeForm = current.checkTypes[key];
        return {
          ...current,
          checkTypes: {
            ...current.checkTypes,
            [key]: {
              ...typeForm,
              limits: typeForm.limits.map((row, rowIndex) =>
                rowIndex === index ? { ...row, ...patch } : row
              ),
            },
          },
        };
      });
    },
    []
  );

  const toggleCheckTypeCategory = useCallback((key: CheckTypeKey, categoryId: string) => {
    setForm((current) => {
      const typeForm = current.checkTypes[key];
      const categoryIds = typeForm.categoryIds.includes(categoryId)
        ? typeForm.categoryIds.filter((id) => id !== categoryId)
        : [...typeForm.categoryIds, categoryId];

      return {
        ...current,
        checkTypes: {
          ...current.checkTypes,
          [key]: { ...typeForm, categoryIds },
        },
      };
    });
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const validationError = validatePricelistForm(form);
      if (validationError) {
        showNotification({ message: validationError, variant: "error" });
        return;
      }

      setSaving(true);
      const payload = formToPricelist(form);

      try {
        if (isCreate) {
          await createPricelist(payload);
          showNotification({ message: "Тариф создан", variant: "success" });
        } else if (pricelistId) {
          await updatePricelist(pricelistId, payload);
          showNotification({ message: "Тариф обновлён", variant: "success" });
        }
        onSaved();
        onClose();
      } catch (err: unknown) {
      } finally {
        setSaving(false);
      }
    },
    [form, isCreate, onClose, onSaved, pricelistId, showNotification]
  );

  const title = isCreate ? "Создать тариф" : isEditing ? "Редактировать тариф" : "Тариф";

  return (
    <ModalPortal>
      <div className="detail-checks-modal-overlay" onClick={onClose}>
        <div
          className="detail-checks-modal detail-checks-modal--pricelist"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="detail-checks-modal-head">
            <h3>{title}</h3>
            <button
              type="button"
              className="detail-tasklist-close"
              onClick={onClose}
              aria-label="Закрыть"
            >
              <X size={18} />
            </button>
          </div>

          {loading ? (
            <div className="pricelist-modal-loading">
              <CircularProgress size="md" />
            </div>
          ) : (
            <>
              <form
                id="pricelist-modal-form"
                className="detail-checks-modal-body create-request-modal-form pricelist-modal-form"
                onSubmit={handleSubmit}
              >
              <div className="pricelist-modal-row pricelist-modal-row--meta">
                <div className="create-request-modal-form-group">
                  <label className="create-request-modal-label">Статус</label>
                  <FormDropdown
                    value={form.status}
                    options={PRICELIST_STATUS_OPTIONS}
                    disabled={readOnly}
                    onChange={(status) => setForm((current) => ({ ...current, status }))}
                  />
                </div>
                <div className="create-request-modal-form-group">
                  <label className="create-request-modal-label" htmlFor="pricelist-name">
                    Название
                  </label>
                  <input
                    id="pricelist-name"
                    type="text"
                    className="create-request-modal-input"
                    value={form.name}
                    disabled={readOnly}
                    onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                  />
                </div>
              </div>

              <div className="create-request-modal-form-group">
                <label className="create-request-modal-label" htmlFor="pricelist-description">
                  Описание
                </label>
                <textarea
                  id="pricelist-description"
                  className="create-request-modal-input pricelist-modal-textarea"
                  value={form.description}
                  disabled={readOnly}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, description: e.target.value }))
                  }
                />
              </div>

              <label
                className={`pricelist-modal-flag${readOnly ? " pricelist-modal-flag--disabled" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={form.visibleForUsers}
                  disabled={readOnly}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      visibleForUsers: e.target.checked,
                    }))
                  }
                />
                <span>Показывать пользователям</span>
              </label>

              <section className="pricelist-modal-section">
                <div className="pricelist-modal-section-head">
                  <h4 className="pricelist-modal-section-title">Стоимость</h4>
                  {!readOnly && (
                    <button
                      type="button"
                      className="pricelist-modal-add-btn"
                      onClick={() =>
                        setForm((current) => ({
                          ...current,
                          priceItemList: [
                            ...current.priceItemList,
                            { period: "month", price: "" },
                          ],
                        }))
                      }
                    >
                      Добавить цену
                    </button>
                  )}
                </div>
                {form.priceItemList.map((row, index) => (
                  <div key={`price-${index}`} className="pricelist-modal-row pricelist-modal-row--price">
                    <div className="create-request-modal-form-group">
                      <label className="create-request-modal-label">Период</label>
                      <FormDropdown
                        value={row.period}
                        options={PAYMENT_PERIOD_OPTIONS}
                        disabled={readOnly}
                        onChange={(period) => updatePriceRow(index, { period })}
                      />
                    </div>
                    <div className="create-request-modal-form-group">
                      <label className="create-request-modal-label">Цена, ₽</label>
                      <input
                        type="number"
                        min={0}
                        className="create-request-modal-input"
                        value={row.price}
                        disabled={readOnly}
                        onChange={(e) => updatePriceRow(index, { price: e.target.value })}
                      />
                    </div>
                    {!readOnly && (
                      <button
                        type="button"
                        className="pricelist-modal-remove-btn"
                        disabled={form.priceItemList.length <= 1}
                        onClick={() =>
                          setForm((current) => ({
                            ...current,
                            priceItemList: current.priceItemList.filter(
                              (_, rowIndex) => rowIndex !== index
                            ),
                          }))
                        }
                        aria-label="Удалить цену"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                ))}
              </section>

              <section className="pricelist-modal-section">
                <h4 className="pricelist-modal-section-title">Типы проверок</h4>
                {CHECK_TYPE_KEYS.map((key) => {
                  const typeForm = form.checkTypes[key];
                  const selectedCategories = categoryOptions.filter((category) =>
                    typeForm.categoryIds.includes(category.id)
                  );

                  return (
                    <div key={key} className="pricelist-modal-check-type">
                      <label
                        className={`pricelist-modal-flag${
                          readOnly ? " pricelist-modal-flag--disabled" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={typeForm.enabled}
                          disabled={readOnly}
                          onChange={(e) => updateCheckType(key, { enabled: e.target.checked })}
                        />
                        <span>{CHECK_TYPE_LABELS[key]}</span>
                      </label>

                      {typeForm.enabled ? (
                        <div className="pricelist-modal-check-type-body">
                          <div className="pricelist-modal-type-limits">
                            <div className="pricelist-modal-section-head">
                              <span className="create-request-modal-label">Лимиты проверок</span>
                              {!readOnly && (
                                <button
                                  type="button"
                                  className="pricelist-modal-add-btn"
                                  onClick={() =>
                                    updateCheckType(key, {
                                      limits: [...typeForm.limits, emptyLimitRow()],
                                    })
                                  }
                                >
                                  Добавить лимит
                                </button>
                              )}
                            </div>
                            {typeForm.limits.map((row, index) => (
                              <div
                                key={`${key}-limit-${index}`}
                                className="pricelist-modal-row pricelist-modal-row--price"
                              >
                                <div className="create-request-modal-form-group">
                                  <label className="create-request-modal-label">Период</label>
                                  <FormDropdown
                                    value={row.period}
                                    options={PAYMENT_PERIOD_OPTIONS}
                                    disabled={readOnly}
                                    onChange={(period) =>
                                      updateCheckTypeLimit(key, index, { period })
                                    }
                                  />
                                </div>
                                <div className="create-request-modal-form-group">
                                  <label className="create-request-modal-label">Количество</label>
                                  <input
                                    type="number"
                                    min={1}
                                    step={1}
                                    className="create-request-modal-input"
                                    value={row.count}
                                    disabled={readOnly}
                                    onChange={(e) =>
                                      updateCheckTypeLimit(key, index, {
                                        count: e.target.value,
                                      })
                                    }
                                  />
                                </div>
                                {!readOnly && (
                                  <button
                                    type="button"
                                    className="pricelist-modal-remove-btn"
                                    disabled={typeForm.limits.length <= 1}
                                    onClick={() =>
                                      updateCheckType(key, {
                                        limits: typeForm.limits.filter(
                                          (_, rowIndex) => rowIndex !== index
                                        ),
                                      })
                                    }
                                    aria-label="Удалить лимит"
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>

                          <div className="pricelist-modal-flags">
                            <label
                              className={`pricelist-modal-flag${
                                readOnly ? " pricelist-modal-flag--disabled" : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={typeForm.schedule}
                                disabled={readOnly}
                                onChange={(e) =>
                                  updateCheckType(key, { schedule: e.target.checked })
                                }
                              />
                              <span>Запуск по расписанию</span>
                            </label>
                            <label
                              className={`pricelist-modal-flag${
                                readOnly ? " pricelist-modal-flag--disabled" : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={typeForm.report}
                                disabled={readOnly}
                                onChange={(e) =>
                                  updateCheckType(key, { report: e.target.checked })
                                }
                              />
                              <span>Отправка отчёта на почту</span>
                            </label>
                          </div>

                          <div className="category-modal-checks">
                            <div className="category-modal-checks-head">
                              <span className="create-request-modal-label">Категории</span>
                              {isEditing ? (
                                <span className="category-modal-checks-hint">
                                  Отметьте категории, доступные для этого типа проверки
                                </span>
                              ) : null}
                            </div>
                            <div className="category-modal-checks-list">
                              {readOnly ? (
                                selectedCategories.length === 0 ? (
                                  <p className="category-modal-checks-empty">
                                    Категории не выбраны
                                  </p>
                                ) : (
                                  <ul className="category-modal-selected-checks">
                                    {selectedCategories.map((category) => (
                                      <li
                                        key={category.id}
                                        className="category-modal-selected-check-item"
                                      >
                                        <span className="category-modal-check-title">
                                          {category.label}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )
                              ) : categoryOptions.length === 0 ? (
                                <p className="category-modal-checks-empty">
                                  Нет доступных категорий
                                </p>
                              ) : (
                                categoryOptions.map((category) => {
                                  const selected = typeForm.categoryIds.includes(category.id);
                                  return (
                                    <label
                                      key={category.id}
                                      className={`category-modal-check-item${
                                        selected ? " category-modal-check-item--selected" : ""
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() =>
                                          toggleCheckTypeCategory(key, category.id)
                                        }
                                      />
                                      <span className="category-modal-check-text">
                                        <span className="category-modal-check-title">
                                          {category.label}
                                        </span>
                                      </span>
                                    </label>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </section>
              </form>

              <div className="detail-checks-modal-footer pricelist-modal-actions">
                {readOnly ? (
                  <button
                    type="button"
                    className="create-request-modal-submit"
                    onClick={() => setIsEditing(true)}
                  >
                    Редактировать
                  </button>
                ) : (
                  <>
                    {!isCreate && (
                      <button
                        type="button"
                        className="pricelist-modal-secondary-btn"
                        disabled={saving}
                        onClick={() => {
                          if (!pricelistId) return;
                          setLoading(true);
                          fetchPricelistById(pricelistId)
                            .then((item) => {
                              setForm(pricelistToForm(item));
                              setIsEditing(false);
                            })
                            .finally(() => setLoading(false));
                        }}
                      >
                        Отмена
                      </button>
                    )}
                    <button
                      type="submit"
                      form="pricelist-modal-form"
                      className="create-request-modal-submit"
                      disabled={saving}
                    >
                      {saving ? <CircularProgress size="sm" /> : isCreate ? "Создать" : "Сохранить"}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};
